import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo, useState } from "react";

import { useScoutProposalHandlers } from "./ScoutProposalContext";
import {
  TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND,
  type ScoutTenantDocumentProposal,
} from "./scoutTenantDocumentProposal";

type Props = {
  proposal: ScoutTenantDocumentProposal;
};

// Below this card width (e.g. inside the Scout popover) the full button labels
// don't fit on one row, so we swap to the compact "Don't use" / "Allow n
// document(s)" variants. Container query keys off the card's own width, not the
// viewport.
const BUTTON_COMPACT_QUERY = "@container (max-width: 460px)";
// Extreme narrow fallback: even the compact labels can't sit side by side, so
// let the buttons wrap and each take a full row.
const BUTTON_WRAP_QUERY = "@container (max-width: 240px)";

function formatFileExtension(fileExtension: string): string {
  return fileExtension.trim().replace(/^\./, "").toUpperCase();
}

function formatSizeMegabytes(sizeMegabytes: number | null): string {
  if (sizeMegabytes === null || !Number.isFinite(sizeMegabytes) || sizeMegabytes <= 0) {
    return "";
  }
  if (sizeMegabytes < 0.1) {
    const kb = Math.max(1, Math.round(sizeMegabytes * 1024));
    return `${kb} KB`;
  }
  if (sizeMegabytes < 10) {
    return `${sizeMegabytes.toFixed(1)} MB`;
  }
  return `${Math.round(sizeMegabytes)} MB`;
}

type SubmittingAction = "confirm" | "decline" | null;

export function ScoutTenantDocumentProposalCard({ proposal }: Props) {
  const handlers = useScoutProposalHandlers(TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND);
  const allDocumentIds = useMemo(
    () => proposal.documents.map((document) => document.id),
    [proposal.documents]
  );
  const [selectedIds, setSelectedIds] = useState<string[]>(allDocumentIds);
  const [hasError, setHasError] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<SubmittingAction>(null);

  const responseStatus = handlers?.respondedProposals[proposal.proposalId];
  const hasHandlers = handlers !== undefined;
  const hasDocuments = proposal.documents.length > 0;
  const isSubmitting = submittingAction !== null;
  const isLockedOut = isSubmitting || !hasHandlers;

  const toggleId = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) {
        if (current.includes(id)) return current;
        return [...current, id];
      }
      return current.filter((entry) => entry !== id);
    });
  };

  const handleConfirm = async () => {
    if (!handlers || isSubmitting) return;
    setHasError(false);
    setSubmittingAction("confirm");
    try {
      await handlers.onConfirm({
        proposalId: proposal.proposalId,
        documentIds: selectedIds,
        totalProposedCount: proposal.documents.length,
      });
    } catch {
      setHasError(true);
      setSubmittingAction(null);
    }
  };

  const handleDecline = async () => {
    if (!handlers || isSubmitting) return;
    setHasError(false);
    setSubmittingAction("decline");
    try {
      await handlers.onDecline({
        proposalId: proposal.proposalId,
        totalProposedCount: proposal.documents.length,
      });
    } catch {
      setHasError(true);
      setSubmittingAction(null);
    }
  };

  if (responseStatus) {
    return (
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {responseStatus === "confirmed"
            ? "Documents added to this conversation."
            : "No problem — I'll continue without those documents."}
        </Typography>
      </Box>
    );
  }

  if (!hasDocuments) {
    return (
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Scout didn't find any matching documents in your document library.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        borderRadius: 4,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        maxWidth: 720,
        containerType: "inline-size",
      }}
    >
      <Stack spacing={2}>
        {proposal.documents.map((document) => {
          const checked = selectedIds.includes(document.id);
          const meta = [
            formatFileExtension(document.fileExtension),
            formatSizeMegabytes(document.sizeMegabytes),
          ]
            .filter((entry) => entry.length > 0)
            .join(" · ");
          return (
            <Box
              key={document.id}
              sx={(theme) => ({
                px: 1.5,
                py: 1.25,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              })}
            >
              <FormControlLabel
                sx={{
                  alignItems: "center",
                  m: 0,
                  width: "100%",
                  minWidth: 0,
                  "& .MuiFormControlLabel-label": { minWidth: 0, flex: 1 },
                }}
                control={
                  <Checkbox
                    checked={checked}
                    disabled={isSubmitting}
                    onChange={(event) => toggleId(document.id, event.target.checked)}
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                  />
                }
                label={
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ width: "100%", minWidth: 0 }}
                  >
                    <DescriptionOutlined fontSize="small" color="primary" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Tooltip title={document.title} enterDelay={500}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {document.title}
                        </Typography>
                      </Tooltip>
                      {meta ? (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {meta}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                }
              />
            </Box>
          );
        })}
        {proposal.missingDocumentIds.length > 0 ? (
          <Alert severity="info" variant="outlined">
            {proposal.missingDocumentIds.length} proposed{" "}
            {proposal.missingDocumentIds.length === 1 ? "id was" : "ids were"} not found in your
            document library and will be skipped.
          </Alert>
        ) : null}
        {hasError ? (
          <Alert severity="error">
            Something went wrong responding to this proposal. Please try again.
          </Alert>
        ) : null}
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          justifyContent="flex-end"
          sx={{ flexWrap: "nowrap", [BUTTON_WRAP_QUERY]: { flexWrap: "wrap" } }}
        >
          <Button
            onClick={handleDecline}
            disabled={isLockedOut}
            sx={{ flexShrink: 0, whiteSpace: "nowrap", [BUTTON_WRAP_QUERY]: { width: "100%" } }}
            startIcon={
              submittingAction === "decline" ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            <Box component="span" sx={{ [BUTTON_COMPACT_QUERY]: { display: "none" } }}>
              Don't use these
            </Box>
            <Box
              component="span"
              sx={{ display: "none", [BUTTON_COMPACT_QUERY]: { display: "inline" } }}
            >
              Don't use
            </Box>
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isLockedOut || selectedIds.length === 0}
            sx={{ flexShrink: 0, whiteSpace: "nowrap", [BUTTON_WRAP_QUERY]: { width: "100%" } }}
            startIcon={
              submittingAction === "confirm" ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            <Box component="span" sx={{ [BUTTON_COMPACT_QUERY]: { display: "none" } }}>
              {`Yes, allow ${selectedIds.length} ${
                selectedIds.length === 1 ? "document" : "documents"
              }`}
            </Box>
            <Box
              component="span"
              sx={{ display: "none", [BUTTON_COMPACT_QUERY]: { display: "inline" } }}
            >
              {`Allow ${selectedIds.length} ${selectedIds.length === 1 ? "document" : "documents"}`}
            </Box>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
